import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  ActionOverlayPopover,
  CloseIcon,
  IconButton,
  OverlayTriggerTooltip,
  PillV2 as Pill,
  PlusIcon,
  Radio,
  Toolbar,
} from '@utilities';

import { getPromptPillTooltip, getPromptPillValue } from '../../../../utilities/helpers';

import {
  comparisonOptions,
  excludedPrompts,
  testPanelSurveyComparisonOptions,
} from './utilities/constants';
import { buildPurchasedAnswer, getPromptLabelGroup } from './utilities/helpers';
import ModalPurchaseVolume from './components/ModalPurchaseVolume';

import styles from './_index.module.scss';

const GroupMadlib = ({
  activePrompt,
  group,
  isReadOnly,
  onClearAnswer,
  onSelectAnswer,
  onSetActivePrompt,
  surveyType,
  updateVisiblePrompts,
}) => {
  const { prompts } = surveyType;
  const { id, promptAnswers } = group;

  const [activeVolumeGroupIndex, setActiveVolumeGroupIndex] = useState(null);
  const [brandPrompt, setBrandPrompt] = useState(null);
  const [initialSelectedVolumeField, setInitialSelectedVolumeField] = useState(null);
  const [isVolumeModalOpen, setIsVolumeModalOpen] = useState(false);
  const [openPopoverIndex, setOpenPopoverIndex] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [visibleCount, setVisibleCount] = useState(1);
  const [volumePrompt, setVolumePrompt] = useState(null);

  const hasInitializedVisibleGroups = useRef(false);
  const isTestPanelSurvey =
    surveyType?.id === 'concept-test-v3-instant-survey-type' ||
    surveyType?.id === 'monadic-concept-test-v3-instant-survey-type';

  const surveyComparisonOptions = isTestPanelSurvey
    ? testPanelSurveyComparisonOptions
    : comparisonOptions;

  const groupedPrompts = useMemo(() => {
    const groups = new Map();
    prompts
      .filter((prompt) => prompt.promptKey && !excludedPrompts.includes(prompt.promptKey))
      .forEach((prompt) => {
        const groupIndex = prompt.behaviorGroup;
        if (!groups.has(groupIndex)) groups.set(groupIndex, []);
        groups.get(groupIndex).push(prompt);
      });
    return Array.from(groups.entries()).sort((a, b) => a[0] - b[0]);
  }, [prompts]);

  const getAnswersForPrompt = useCallback(
    (prompt) =>
      promptAnswers
        .filter(
          (promptAnswer) =>
            !promptAnswer._delete &&
            promptAnswer.promptId === prompt?.id &&
            promptAnswer.fieldType === 'NUMERIC'
        )
        .map((promptAnswer) => ({
          ...promptAnswer,
          name:
            prompt?.attributes?.find(({ field }) => field === promptAnswer.field)?.name ||
            promptAnswer.field,
          operatorLabel:
            surveyComparisonOptions.find(({ value }) => value === promptAnswer.operator)?.label ||
            promptAnswer.operator,
          valueLabel:
            promptAnswer.values.length > 1
              ? `${promptAnswer.values[0]} and ${promptAnswer.values[1]}`
              : promptAnswer.values[0],
        })),
    [promptAnswers]
  );

  useEffect(() => {
    const newSelectedOptions = {};
    const answeredGroupIndexes = new Set();

    promptAnswers.forEach(({ _delete, promptId }) => {
      if (_delete) return;

      groupedPrompts.forEach(([groupIndex, groupPrompts], index) => {
        const purchasePrompt = groupPrompts.find(({ name }) => name === 'Purchased');
        if (purchasePrompt?.id === promptId) {
          answeredGroupIndexes.add(index);
          const valueId = promptAnswers.find((a) => a.promptId === promptId)?.values?.[0]?.id;
          if (valueId?.includes('was_purchased:1')) newSelectedOptions[groupIndex] = 'purchased';
          else if (valueId?.includes('was_purchased:2')) {
            newSelectedOptions[groupIndex] = 'not_purchased';
          }
        } else if (groupPrompts.some((prompt) => prompt.id === promptId)) {
          answeredGroupIndexes.add(index);
        }
      });
    });

    setSelectedOptions(newSelectedOptions);

    const count = Math.max(1, answeredGroupIndexes.size);
    if (count > visibleCount) {
      setVisibleCount(count);
    }

    if (!isReadOnly) {
      updateVisiblePrompts(id, (prevVisible) => {
        const newVisible = new Set(prevVisible);
        groupedPrompts.slice(0, count).forEach(([, prompts]) => {
          prompts.forEach(({ id }) => newVisible.add(id));
        });
        return newVisible;
      });
      hasInitializedVisibleGroups.current = true;
    }
  }, [groupedPrompts, id, isReadOnly, promptAnswers, updateVisiblePrompts]);

  useEffect(() => {
    if (isReadOnly) return;

    const groupIndex = visibleCount;
    const promptsInGroup = groupedPrompts[groupIndex - 1]?.[1];
    const purchasePrompt = promptsInGroup?.find(({ name }) => name === 'Purchased');

    const hasAnswerForPurchasePrompt = promptAnswers.some(
      ({ _delete, promptId }) => !_delete && promptId === purchasePrompt?.id
    );

    if (purchasePrompt && !hasAnswerForPurchasePrompt && !selectedOptions[groupIndex]) {
      const answers = buildPurchasedAnswer({ isPurchased: true, prompt: purchasePrompt });
      onSelectAnswer(answers, purchasePrompt);
      setSelectedOptions((prev) => ({ ...prev, [groupIndex]: 'purchased' }));
    }
  }, [groupedPrompts, isReadOnly, promptAnswers, selectedOptions, visibleCount]);

  const closeVolumeModal = () => {
    setInitialSelectedVolumeField(null);
    setIsVolumeModalOpen(false);
  };

  const handleRadioChange = (isPurchased, prompt, groupIndex) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [groupIndex]: isPurchased ? 'purchased' : 'not_purchased',
    }));

    const answers = buildPurchasedAnswer({ isPurchased, prompt });

    onSelectAnswer(answers, prompt);

    if (!isPurchased) {
      const promptsInGroup = groupedPrompts[groupIndex - 1][1];
      const volumePrompt = promptsInGroup.find((p) => p.insightsKey === 'purchase_volume');
      const brandPrompt = promptsInGroup.find((p) => p.insightsKey === 'unique_brands');

      if (volumePrompt) onClearAnswer(volumePrompt.id);
      if (brandPrompt) onClearAnswer(brandPrompt.id);
    }
  };

  const handleRemoveGroup = (groupIndexToRemove) => {
    if (groupIndexToRemove === 1) return;
    const isLast = groupIndexToRemove === visibleCount;

    if (isLast) {
      const promptsToRemove = groupedPrompts[groupIndexToRemove - 1][1];
      promptsToRemove.forEach(({ id }) => onClearAnswer(id));
      updateVisiblePrompts(id, (prevVisible) => {
        const updated = new Set(prevVisible);
        promptsToRemove.forEach(({ id }) => updated.delete(id));
        return updated;
      });
      setVisibleCount((prev) => Math.max(1, prev - 1));
    } else {
      groupedPrompts.forEach(([, prompts], index) => {
        if (index !== 0) prompts.forEach(({ id }) => onClearAnswer(id));
      });
      updateVisiblePrompts(id, (prevVisible) => {
        const updated = new Set(prevVisible);
        groupedPrompts.forEach(([, prompts], index) => {
          if (index !== 0) prompts.forEach(({ id }) => updated.delete(id));
        });
        return updated;
      });
      setVisibleCount(1);
    }
  };

  const openVolumeModal = (groupIndex, field = null) => {
    const promptsInGroup = groupedPrompts[groupIndex - 1][1];
    const brandPromptInGroup = promptsInGroup.find((p) => p.insightsKey === 'unique_brands');
    const volumePromptInGroup = promptsInGroup.find((p) => p.insightsKey === 'purchase_volume');

    setVolumePrompt(volumePromptInGroup || null);
    setBrandPrompt(brandPromptInGroup || null);

    setActiveVolumeGroupIndex(groupIndex);
    setInitialSelectedVolumeField(field);
    setIsVolumeModalOpen(true);
  };
  const renderPromptContent = (prompts, groupIndex) => {
    const labelGroups = prompts.reduce((acc, prompt) => {
      if (['Purchased', 'purchase_volume', 'unique_brands'].includes(prompt.name)) return acc;
      const label = getPromptLabelGroup(prompt);
      if (label) {
        acc[label] = acc[label] || [];
        acc[label].push(prompt);
      }
      return acc;
    }, {});

    const isNotPurchased = selectedOptions[groupIndex] === 'not_purchased';

    const volumePrompt = prompts.find(({ insightsKey }) => insightsKey === 'purchase_volume');
    const brandPrompt = prompts.find(({ insightsKey }) => insightsKey === 'unique_brands');

    const volumePromptAnswers = volumePrompt ? getAnswersForPrompt(volumePrompt) : [];
    const brandPromptAnswers = brandPrompt ? getAnswersForPrompt(brandPrompt) : [];

    const hasCustomFilters = volumePrompt || brandPrompt;

    return (
      <>
        {Object.entries(labelGroups).map(([label, labelPrompts]) => (
          <div className={styles['group-madlib-label-group']} key={label}>
            {label && <div className={styles['group-madlib-label-title']}>{label}</div>}
            <div className={styles['group-madlib-pills-row']}>
              {labelPrompts.map((prompt) => {
                const currentPromptAnswers = promptAnswers.filter(
                  ({ _delete, promptId }) => !_delete && promptId === prompt.id
                );
                const tooltip = getPromptPillTooltip({ prompt, promptAnswers });
                const value = getPromptPillValue({ prompt, promptAnswers });

                return (
                  <OverlayTriggerTooltip content={tooltip} isFullWidth key={prompt.id}>
                    <Pill
                      data-testid={`group-madlib-pill-${prompt.id}`}
                      isActive={prompt === activePrompt}
                      isCompleted={isReadOnly}
                      isModified={currentPromptAnswers.length > 0}
                      isRequired={prompt.isRequired}
                      onClick={() => onSetActivePrompt(prompt)}
                      onClose={() => onClearAnswer(prompt.id)}
                      value={value}
                    />
                  </OverlayTriggerTooltip>
                );
              })}
            </div>
          </div>
        ))}

        {hasCustomFilters && (
          <div className={styles['group-madlib-label-group-volume']}>
            {!isNotPurchased &&
              [...volumePromptAnswers, ...brandPromptAnswers]
                .filter((answer) => answer.valueLabel)
                .map((answer) => (
                  <div key={`${answer.field}:${answer.operator}`}>
                    <div className={styles['group-madlib-label-title']}>{answer.name}</div>
                    <div className={styles['group-madlib-pills-row']}>
                      <Pill
                        isCompleted={isReadOnly}
                        isModified
                        onClick={() => openVolumeModal(groupIndex, answer)}
                        onClose={() => onClearAnswer(answer.promptId, answer.field)}
                        value={`${answer.operatorLabel} ${answer.valueLabel}`}
                      />
                    </div>
                  </div>
                ))}
            {!isReadOnly && (
              <div className={styles['group-madlib-label-group-volume-plus']}>
                <div className={styles['group-madlib-pills-row']}>
                  <OverlayTriggerTooltip
                    content={
                      isNotPurchased
                        ? 'Custom filters aren’t available when ‘have NOT purchased’ is selected.'
                        : 'Add Custom Filters'
                    }
                  >
                    <Pill
                      isCompleted={isNotPurchased}
                      onClick={() => openVolumeModal(groupIndex, volumePrompt || brandPrompt)}
                      value={<PlusIcon />}
                    />
                  </OverlayTriggerTooltip>
                </div>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className={styles['group-madlib']}>
      {groupedPrompts.slice(0, visibleCount).map(([groupIndex, groupPrompts], index) => {
        const isFirst = index === 0;
        const isLastVisibleGroup = groupIndex === visibleCount;
        const purchasePrompt = groupPrompts.find(({ name }) => name === 'Purchased');

        const handleDuplicateBehavior = () => {
          const nextIndex = groupedPrompts.findIndex(([, prompts], index) => {
            if (index < visibleCount) return false;
            return true;
          });

          if (nextIndex === -1) return;

          const targetPrompts = groupedPrompts[nextIndex][1];
          const duplicatedAnswers = [];

          groupPrompts.forEach((groupPrompt) => {
            const matchingAnswers = promptAnswers.filter(
              (promptAnswer) => promptAnswer.promptId === groupPrompt.id && !promptAnswer._delete
            );

            matchingAnswers.forEach((answer) => {
              const matchInTarget = targetPrompts.find(
                (targetPrompt) => targetPrompt.insightsKey === groupPrompt.insightsKey
              );

              if (matchInTarget) {
                const { id, ...rest } = answer;
                duplicatedAnswers.push({
                  ...rest,
                  promptId: matchInTarget.id,
                });
              }
            });
          });

          if (duplicatedAnswers.length > 0) {
            setVisibleCount((prev) => Math.max(prev, nextIndex + 1));
            onSelectAnswer(duplicatedAnswers, null, true);
            setOpenPopoverIndex(null);
          }
        };

        const handleClearBehaviorSelections = () => {
          groupPrompts?.forEach(({ id }) => onClearAnswer(id));
          setOpenPopoverIndex(null);
        };

        const hasSelectedPrompt = promptAnswers.some(
          (answer) =>
            !answer._delete && groupPrompts.some((prompt) => answer.promptId === prompt.id)
        );

        return (
          <div className={styles['group-madlib-header']} key={groupIndex}>
            {!isFirst && (
              <div className={styles['group-madlib-and-separator']}>
                <span className={styles['group-madlib-and-label']}>AND</span>
              </div>
            )}

            <Toolbar
              className={styles['group-madlib-toolbar']}
              tools={
                !isReadOnly
                  ? [
                      !isFirst && isLastVisibleGroup && (
                        <IconButton
                          aria-label="Remove Group"
                          className={styles['group-madlib-remove-group']}
                          key="remove-group"
                          onClick={() => handleRemoveGroup(groupIndex)}
                        >
                          <CloseIcon />
                        </IconButton>
                      ),
                      <ActionOverlayPopover
                        isActive={openPopoverIndex === groupIndex}
                        key="behavior-actions"
                        onClick={() =>
                          setOpenPopoverIndex(openPopoverIndex === groupIndex ? null : groupIndex)
                        }
                        onHide={() => setOpenPopoverIndex(null)}
                      >
                        <ul>
                          <li>
                            <button
                              disabled={visibleCount >= groupedPrompts.length || !hasSelectedPrompt}
                              onClick={handleDuplicateBehavior}
                            >
                              <span>Duplicate</span>
                            </button>
                          </li>
                          <hr />
                          <li>
                            <button
                              disabled={!hasSelectedPrompt}
                              onClick={handleClearBehaviorSelections}
                            >
                              <span>Clear Selections</span>
                            </button>
                          </li>
                        </ul>
                      </ActionOverlayPopover>,
                    ]
                  : []
              }
            >
              {isFirst || isTestPanelSurvey ? (
                <p>
                  Panelists that <strong>have purchased</strong>{' '}
                </p>
              ) : (
                <div className={styles['group-madlib-radio-row']}>
                  {isReadOnly ? (
                    <div className={styles['group-madlib-radio-label']}>
                      {selectedOptions[groupIndex] === 'purchased'
                        ? 'have purchased'
                        : selectedOptions[groupIndex] === 'not_purchased'
                          ? 'have NOT purchased'
                          : ''}
                    </div>
                  ) : (
                    <>
                      <Radio
                        isChecked={selectedOptions[groupIndex] === 'purchased'}
                        key="purchased"
                        label="have purchased"
                        onChange={() => handleRadioChange(true, purchasePrompt, groupIndex)}
                        value="purchased"
                      />
                      <Radio
                        isChecked={selectedOptions[groupIndex] === 'not_purchased'}
                        key="not_purchased"
                        label="have NOT purchased"
                        onChange={() => handleRadioChange(false, purchasePrompt, groupIndex)}
                        value="not_purchased"
                      />
                    </>
                  )}
                </div>
              )}
            </Toolbar>

            <div className={styles['group-madlib-prompts']}>
              {renderPromptContent(groupPrompts, groupIndex)}
            </div>
          </div>
        );
      })}

      {!isReadOnly && visibleCount < groupedPrompts.length && (
        <>
          <hr />
          <IconButton
            data-testid="group-madlib-add-behavior-button"
            onClick={() => setVisibleCount((prev) => prev + 1)}
          >
            <PlusIcon /> Add another<strong>Purchase Behavior</strong>
          </IconButton>
        </>
      )}

      {isVolumeModalOpen && activeVolumeGroupIndex !== null && (volumePrompt || brandPrompt) && (
        <ModalPurchaseVolume
          currentValues={promptAnswers.filter(
            ({ _delete, promptId }) =>
              !_delete && (promptId === volumePrompt?.id || promptId === brandPrompt?.id)
          )}
          initialSelectedField={initialSelectedVolumeField}
          onClose={closeVolumeModal}
          onSave={(newAnswers) => {
            onSelectAnswer(newAnswers, volumePrompt);
          }}
          surveyComparisonOptions={surveyComparisonOptions}
          uniqueBrandsPrompt={brandPrompt}
          volumePrompt={volumePrompt}
        />
      )}
    </div>
  );
};

GroupMadlib.propTypes = {
  activePrompt: PropTypes.object,
  group: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  onClearAnswer: PropTypes.func,
  onSelectAnswer: PropTypes.func,
  onSetActivePrompt: PropTypes.func,
  surveyType: PropTypes.object.isRequired,
  updateVisiblePrompts: PropTypes.func,
};

export default GroupMadlib;
